import { type WorkbenchService } from '@affine/core/modules/workbench';
import { LiveData, Service } from '@toeverything/infra';

import type { DocCommentEntity } from '../entities/doc-comment';

/** A place that shows a doc's comments instead of the workbench sidebar. */
export interface CommentSurface {
  open(): void;
}

export class CommentPanelService extends Service {
  constructor(private readonly workbenchService: WorkbenchService) {
    super();
  }

  private readonly activePendingWatchers = new Set<() => void>();

  /**
   * The surface that shows the comments while it is there: a full PDF reader
   * lies over the doc and its sidebar, so it carries the comment list itself.
   */
  readonly surface$ = new LiveData<CommentSurface | null>(null);

  /**
   * Makes `surface` the place for comments until the returned function is
   * called.
   */
  claimSurface(surface: CommentSurface): () => void {
    this.surface$.next(surface);
    return () => {
      if (this.surface$.value === surface) this.surface$.next(null);
    };
  }

  /**
   * Watch for pending comments on a doc comment entity and open the sidebar automatically
   */
  watchForPendingComments(entity: DocCommentEntity): () => void {
    const subscription = entity.pendingComment$.subscribe(pendingComment => {
      // If we have a new pending comment, open the comment panel
      if (pendingComment) {
        this.openCommentPanel();
      }
    });

    const dispose = () => {
      subscription.unsubscribe();
      this.activePendingWatchers.delete(dispose);
    };

    this.activePendingWatchers.add(dispose);
    return dispose;
  }

  /**
   * Show the comments: on the claimed surface, otherwise open the sidebar and
   * activate the comment tab
   */
  openCommentPanel(): void {
    const surface = this.surface$.value;
    if (surface) {
      surface.open();
      return;
    }
    const workbench = this.workbenchService.workbench;
    const activeView = workbench.activeView$.value;

    if (activeView) {
      workbench.openSidebar();
      activeView.activeSidebarTab('comment');
    }
  }

  override dispose(): void {
    // Clean up all active watchers
    for (const dispose of this.activePendingWatchers) {
      dispose();
    }
    this.activePendingWatchers.clear();
    super.dispose();
  }
}
